import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";

const services = [
  {
    title: "Vector Art Services",
    description:
      "Transform your brand with precision‑crafted vector illustrations, logos, and scalable designs that look impeccable on any medium.",
    path: "/pricing/vector",
    theme: {
      bar: "from-orange-500 to-amber-500",
      btn: "bg-slate-900 hover:bg-orange-600",
      check: "bg-orange-500",
      hover: "hover:border-orange-300 hover:shadow-orange-100",
      ring: "focus-visible:ring-orange-500/50",
    },
    features: ["Scalable Vector Graphics", "Brand Identity Systems", "Multi-format Delivery"],
  },
  {
    title: "Embroidery Digitization",
    description:
      "Expert conversion of your artwork into machine‑ready embroidery files with optimized stitch paths and QA built in.",
    path: "/pricing/digitization",
    theme: {
      bar: "from-red-500 to-rose-500",
      btn: "bg-slate-900 hover:bg-red-600",
      check: "bg-red-500",
      hover: "hover:border-red-300 hover:shadow-red-100",
      ring: "focus-visible:ring-red-500/50",
    },
    features: ["Machine-Ready Files", "Quality Assurance", "Multiple Format Support"],
  },
  {
    title: "Custom Patches",
    description:
      "Distinctive patch design and production—premium materials, on‑brand embroidery, and consistent results for teams and events.",
    path: "/pricing/patches",
    theme: {
      bar: "from-amber-500 to-orange-500",
      btn: "bg-slate-900 hover:bg-amber-600",
      check: "bg-amber-500",
      hover: "hover:border-amber-300 hover:shadow-amber-100",
      ring: "focus-visible:ring-amber-500/50",
    },
    features: ["Premium Materials", "Corporate Branding", "Custom Designs"],
  },
];

export default function FeaturedServices() {
  const reduceMotion = useReducedMotion();

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: reduceMotion ? 0.04 : 0.12 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <section
      aria-labelledby="featured-services-heading"
      className="relative w-full overflow-hidden bg-white py-20 sm:py-24 lg:py-32"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-orange-200/40 blur-3xl" />
        <div className="absolute -bottom-40 -right-24 h-96 w-96 rounded-full bg-amber-200/40 blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-6xl px-6">
        <Header item={item} />

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          {services.map((s) => (
            <motion.div key={s.title} variants={item} className="h-full">
              <ServiceCard {...s} />
            </motion.div>
          ))}
        </motion.div>

        <CallToAction reduceMotion={reduceMotion} />
      </div>
    </section>
  );
}

function Header({ item }) {
  return (
    <motion.header
      variants={item}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      className="mb-14 grid grid-cols-1 items-end gap-8 sm:mb-20 lg:grid-cols-2 lg:gap-16"
    >
      <div>
        <p className="mb-5 inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-4 py-1.5 text-xs font-semibold tracking-wide text-orange-800">
          Premium Design Solutions
        </p>
        <h2
          id="featured-services-heading"
          className="text-balance text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
        >
          Excellence in{" "}
          <span className="bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 bg-clip-text text-transparent">
            Creative Design
          </span>
        </h2>
      </div>
      <p className="text-pretty text-lg leading-relaxed text-slate-600 sm:text-xl">
        Elevate your brand with a focused suite of professional design services—crafted with precision, delivered with consistency.
      </p>
    </motion.header>
  );
}

function ServiceCard({ title, description, path, theme, features }) {
  return (
    <article
      className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 pt-10 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${theme.hover}`}
    >
      <div className={`absolute left-0 top-0 h-1.5 w-full bg-gradient-to-r ${theme.bar}`} />

      <h3 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h3>
      <p className="mt-4 leading-relaxed text-slate-600">{description}</p>

      <div className="my-6 border-t border-dashed border-slate-300" />

      <ul className="space-y-3">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm">
            <span className={`mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full ${theme.check}`}>
              <svg className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            <span className="font-medium text-slate-700">{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-8">
        <Link
          to={path}
          aria-label={`Explore ${title}`}
          className={`inline-flex w-full items-center justify-center rounded-xl px-5 py-3.5 font-semibold text-white transition-colors focus:outline-none focus-visible:ring-4 ${theme.btn} ${theme.ring}`}
        >
          Explore Service
          <svg
            className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
          </svg>
        </Link>
      </div>
    </article>
  );
}

function CallToAction({ reduceMotion }) {
  return (
    <div className="relative mt-20 overflow-hidden rounded-[2rem] bg-slate-900 px-6 py-14 text-center text-white sm:mt-24 sm:px-12 sm:py-20">
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full bg-orange-500/30 blur-3xl"
        animate={reduceMotion ? undefined : { scale: [1, 1.15, 1], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-3 rounded-[1.5rem] border-2 border-dashed border-orange-400/30"
      />

      <div className="relative z-10">
        <h3 className="text-balance text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
          Let’s create something{" "}
          <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
            extraordinary
          </span>
        </h3>
        <p className="mx-auto mt-5 max-w-2xl text-pretty text-lg leading-relaxed text-slate-300">
          Partner with a team that understands your vision and delivers beyond expectations—on time, on brand, and on budget.
        </p>

        <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link
            to="/contact"
            className="inline-flex items-center justify-center rounded-xl bg-orange-500 px-7 py-3.5 font-semibold text-white transition-colors hover:bg-orange-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-400/50"
          >
            Start Your Project
            <svg className="ml-2 h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </Link>
          <Link
            to="/portfolio"
            className="inline-flex items-center justify-center rounded-xl border-2 border-white/70 px-7 py-3.5 font-semibold text-white transition-colors hover:bg-white hover:text-slate-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40"
          >
            View Our Work
            <svg className="ml-2 h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M2.5 12C4.5 7.5 8 5 12 5s7.5 2.5 9.5 7c-2 4.5-5.5 7-9.5 7s-7.5-2.5-9.5-7Z" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
}